import { Dialog, Theme } from '@busybusy/webapp-react-ui';
import { PositionUpdateInput } from '__generated__/graphql';
import classNames from 'classnames';
import { DetailsItem } from 'components';
import ProDetailsItem from 'components/foundation/details-item/ProDetailsItem/ProDetailsItem';
import SingleCheckFieldForm from 'containers/settings/company-profile/forms/SingleFieldForm/SingleCheckFieldForm';
import { useToastDispatch } from 'contexts/ToastContext';
import { useOpenable, usePositionUpdate } from 'hooks';
import { castArray } from 'lodash';
import { ClassName } from 'types/ClassName';
import IPosition, { IPositionBooleanKey } from 'types/Position';
import { t } from 'utils/localize';

export interface IPositionDetailsBooleanDetailProps {
  className?: ClassName;
  position: IPosition;
  title: string;
  subtitle: string;
  positionKey: IPositionBooleanKey | IPositionBooleanKey[];
  requirePro?: boolean;
  editable: boolean;
  onUpdate: () => Promise<void>;
}

const PositionDetailsBooleanDetail = ({
  className,
  position,
  title,
  subtitle,
  positionKey,
  requirePro = false,
  editable,
  onUpdate,
}: IPositionDetailsBooleanDetailProps) => {
  const [, update] = usePositionUpdate();
  const editTitleDialog = useOpenable();
  const toastDispatch = useToastDispatch();

  const classes = classNames('position-details-boolean-detail', className);
  const DetailsItemComponent = requirePro ? ProDetailsItem : DetailsItem;

  const keys = castArray(positionKey);
  const positionValue = keys.every((key) => position[key]);

  async function onFormSubmit(value: boolean) {
    const shouldUpdate = keys.some((key) => position[key] !== value);
    if (shouldUpdate) {
      try {
        const updatedPosition = keys.reduce<PositionUpdateInput>(
          (acc, cur) => {
            // If they're scoped by group they cannot manage employee groups
            if (cur === 'scopeByGroup' && value) {
              return { ...acc, [cur]: value, manageEmployeeGroups: false };
            }
            return { ...acc, [cur]: value };
          },
          {
            id: position.id,
          }
        );
        await update(updatedPosition);
        onUpdate();
      } catch (e) {
        toastDispatch({
          type: 'OPEN',
          payload: { theme: Theme.DANGER, label: t('Unable to update position. Please try again later.') },
        });
      }
    }

    editTitleDialog.close();
  }

  return (
    <>
      <DetailsItemComponent
        title={title}
        onClick={editTitleDialog.open}
        value={positionValue ? t('Yes') : t('No')}
        hint={subtitle}
        className={classes}
        disabled={!editable}
      />
      <Dialog position={'center'} isOpen={editTitleDialog.isOpen} onClose={editTitleDialog.close}>
        <SingleCheckFieldForm
          title={title}
          subtitle={subtitle}
          initialValue={positionValue}
          onSubmit={onFormSubmit}
          className="mt-4 p-8"
        />
      </Dialog>
    </>
  );
};

export default PositionDetailsBooleanDetail;
